import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../contexts/ContextoWorkspace';
import { presupuestosService } from '../services/api/presupuestos.service';

export interface Presupuesto {
  id: string;
  codigo: string;
  nombre: string;
}

export interface PresupuestoInput {
  codigo: string;
  nombre: string;
}

interface UsePresupuestosReturn {
  presupuestos: Presupuesto[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  crearPresupuesto: (input: PresupuestoInput) => Promise<Presupuesto>;
}

export function usePresupuestos(): UsePresupuestosReturn {
  const { empresaId } = useWorkspace();
  const [presupuestos, setPresupuestos] = useState<Presupuesto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esCargandoRef = useRef(false);

  const scopeReady = empresaId != null && empresaId.trim() !== '';

  const refresh = useCallback(async () => {
    if (!scopeReady || !empresaId) {
      setPresupuestos([]);
      return;
    }
    if (esCargandoRef.current) return;
    esCargandoRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const response = await presupuestosService.getAll(empresaId);
      const mapped: Presupuesto[] = (response.data || []).map((item) => ({
        id: item.id,
        codigo: item.codigo,
        nombre: item.nombre,
      }));
      setPresupuestos(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los presupuestos');
    } finally {
      setLoading(false);
      esCargandoRef.current = false;
    }
  }, [empresaId, scopeReady]);

  useEffect(() => {
    void refresh();
  }, [empresaId, scopeReady]);

  const crearPresupuesto = useCallback(async (input: PresupuestoInput): Promise<Presupuesto> => {
    if (!scopeReady || !empresaId) throw new Error('Selecciona una empresa para registrar presupuestos');
    setLoading(true);
    setError(null);
    try {
      const response = await presupuestosService.create({
        empresaId,
        codigo: input.codigo,
        nombre: input.nombre,
      });
      const created: Presupuesto = {
        id: response.data.id,
        codigo: response.data.codigo,
        nombre: response.data.nombre,
      };
      setPresupuestos((prev) => [...prev, created]);
      return created;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo crear el presupuesto';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [empresaId, scopeReady]);

  return { presupuestos, loading, error, refresh, crearPresupuesto };
}
